import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Chip, 
  Button,
  Divider,
  CircularProgress
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import matter from 'gray-matter';
import { Buffer } from 'buffer';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { alpha } from '@mui/material/styles';
import { color, radius, r } from '../../theme/tokens';
import { blogPostsData } from './blogData';
import { logAnalyticsEvent } from '../../utils/analytics';
import usePageReadTracking from '../../hooks/usePageReadTracking';
import NewsletterSignup from '../common/NewsletterSignup';
import config from '../../config';
import './BlogPost.css';

// Polyfill Buffer for gray-matter
window.Buffer = Buffer;

const EDGE_FN_URL = `${config.EDGE_FUNCTION_URL}/getBlogPosts`;

// Strip leading H1/H2 heading — title is already displayed from metadata above content
const stripLeadingTitle = (content) =>
  content.replace(/^\s*#{1,2}\s+\**[^*\n]+\**\n+/, '');

async function fetchFromEdgeFunction(slug) {
  const res = await fetch(`${EDGE_FN_URL}?slug=${encodeURIComponent(slug)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Edge function returned ${res.status}`);
  const data = await res.json();
  if (!data || !data.slug) return null;
  return {
    ...data,
    content: stripLeadingTitle(data.content || ''),
    isMarkdown: true,
  };
}

async function fetchFromMarkdownFile(slug) {
  const res = await fetch(`/blog/posts/${slug}.md`);
  if (!res.ok) throw new Error('Post not found in markdown files');
  const markdown = await res.text();
  const { data, content } = matter(markdown);
  return {
    ...data,
    content: stripLeadingTitle(content),
    isMarkdown: true,
  };
}

function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const contentRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setPost(null);

    async function loadPost() {
      // Tier 1: Supabase edge function (when flag is on)
      if (config.FEATURES.USE_SUPABASE_BLOG) {
        try {
          const edgePost = await fetchFromEdgeFunction(slug);
          if (!cancelled && edgePost) {
            setPost(edgePost);
            setLoading(false);
            return;
          }
        } catch (err) {
          console.warn('getBlogPosts edge function failed, falling back:', err.message);
        }
      }

      // Tier 2: static markdown file
      try {
        const mdPost = await fetchFromMarkdownFile(slug);
        if (!cancelled) {
          setPost(mdPost);
          setLoading(false);
          return;
        }
      } catch (err) {
        console.warn('Markdown file not found, falling back to blogData.js:', err.message);
      }

      // Tier 3: bundled blogData.js
      const found = blogPostsData.find(p => p.slug === slug);
      if (!cancelled) {
        if (found) {
          setPost({ ...found, isMarkdown: false });
          setLoading(false);
        } else {
          console.error('Blog post not found:', slug);
          navigate('/blog');
        }
      }
    }

    loadPost();
    return () => { cancelled = true; };
  }, [slug, navigate]);

  useEffect(() => {
    if (post) {
      logAnalyticsEvent('view_blog_post', {
        blog_slug: post.slug || slug,
        blog_title: post.title,
        blog_author: post.author,
        blog_categories: post.categories ? post.categories.join(', ') : '',
        blog_read_time: post.readTime
      });
    }
  }, [post, slug]);

  usePageReadTracking({
    surface: 'blog_post',
    contentId: post ? (post.slug || slug) : undefined,
    contentTitle: post?.title,
    contentRef: contentRef,
    enabled: !!post,
  });

  const handleCreateStackClick = () => {
    if (post) {
      logAnalyticsEvent('click_blog_cta_create_stack', {
        blog_slug: post.slug || slug,
        blog_title: post.title,
        cta_location: 'blog_post_bottom'
      });
    }
    navigate('/create-stack');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!post) return null;

  return (
    <Box sx={{ py: { xs: 4, md: 8 }, bgcolor: alpha(color.accent, 0.05) }}>
      <Helmet>
        <title>{`${post.title} | myStack Blog`}</title>
        <meta name="description" content={post.excerpt} />
      </Helmet>
      
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ mb: 4, color: color.evergreen }}
        >
          Back to Blog
        </Button>
        
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h1" 
            sx={{
              fontSize: { xs: '1.8rem', md: '2.5rem' },
              fontWeight: 700,
              color: color.evergreen,
              mb: 3
            }}
          >
            {post.title}
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {post.date} • {post.readTime}
            </Typography>
            
            {post.author && post.author.trim() !== '' && (
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                By {post.author}
              </Typography>
            )}
          </Box>
          
          {post.image && (
            <Box 
              component="img" 
              src={post.image} 
              alt={post.title}
              sx={{ 
                width: '100%', 
                borderRadius: radius.lg,
                mb: 3,
                maxHeight: r(500),
                objectFit: 'cover'
              }}
            />
          )}
          
          {post.categories && post.categories.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1.5, mb: 4, mt: 2, flexWrap: 'wrap' }}>
              {post.categories.map((category, index) => (
                <Chip 
                  key={index} 
                  label={category} 
                  size="small"
                  sx={{ 
                    bgcolor: alpha(color.accent, 0.1),
                    color: color.evergreen,
                    fontWeight: 500
                  }} 
                />
              ))}
            </Box>
          )}
        </Box>
        
        <Divider sx={{ mb: 4 }} />
        
        {post.isMarkdown ? (
          <Box className="blog-content" ref={contentRef}>
            {/* rehype-raw is required for legacy HTML posts (ids 1-5 in blogData.js are raw HTML)
                and for inline HTML from Google Docs exports (e.g. <sup>, <br/>, &nbsp;).
                XSS risk is acceptable: content is exclusively written by authenticated admins
                with direct Supabase access — the same trust level as a database admin. */}
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
              {post.content}
            </ReactMarkdown>
          </Box>
        ) : (
          <Box
            className="blog-content"
            ref={contentRef}
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        )}
        
        {post.ctaText && (
          <>
            <Divider sx={{ my: 4 }} />
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleCreateStackClick}
                sx={{ 
                  px: { xs: 4, md: 5 },
                  py: { xs: 1.5, md: 2 },
                  borderRadius: '0.75rem',
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  fontWeight: 600,
                  // Was iOS blue with a blue-tinted shadow. The brief locks one
                  // action colour — the signal accent — with dark ink on the
                  // fill (white fails AA on it at body size). Matches the
                  // landing page's trial CTA.
                  background: color.accent,
                  color: color.accentInk,
                  boxShadow: `0 0.5rem 1.5rem ${alpha(color.accent, 0.2)}`,
                  transition: 'all 0.3s ease',
                  textTransform: 'none',
                  '&:hover': {
                    background: color.accentHover,
                    transform: 'translateY(-0.125rem)',
                    boxShadow: `0 0.75rem 2rem ${alpha(color.accent, 0.25)}`,
                  }
                }}
              >
                {post.ctaText}
              </Button>
            </Box>
          </>
        )}
      </Container>
      
      <NewsletterSignup />
    </Box>
  );
}

export default BlogPostPage;
